// src/pages/ResetPasswordPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

// Password strength indicator
function getPasswordStrength(password: string): { strength: number; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
  if (password.length === 0) {
    return { strength: 0, label: '', color: 'info' };
  }
  
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  
  if (strength <= 2) {
    return { strength: 20, label: 'Weak', color: 'error' };
  } else if (strength === 3) {
    return { strength: 40, label: 'Fair', color: 'warning' };
  } else if (strength === 4) {
    return { strength: 60, label: 'Good', color: 'info' };
  } else {
    return { strength: 100, label: 'Strong', color: 'success' };
  }
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  // @ts-ignore - intentionally unused
  const _navigate = useNavigate();
  const { resetPassword, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{ strength: number; label: string; color: 'error' | 'warning' | 'info' | 'success' }>({ strength: 0, label: '', color: 'info' });

  useEffect(() => {
    // Get email and token from URL query parameters
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    if (tokenParam) {
      setToken(tokenParam);
    }

    // If required parameters are missing, show error
    if (!emailParam || !tokenParam) {
      setError('Invalid or missing reset link. Please use the link from your email.');
    }
  }, [searchParams]);

  useEffect(() => {
    setPasswordStrength(getPasswordStrength(newPassword));
  }, [newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email || !token) {
      setError('Email and token are required. Please use the link from your email.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      await resetPassword({
        email,
        token,
        newPassword,
        confirmPassword,
      });
      // Navigation is handled by AuthContext after successful password reset
    } catch (err: any) {
      const errorMessage = err?.message || 'Password reset failed. The link may have expired.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Reset Password
          </Typography>
          
          {(error || authError) && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error || authError}
            </Alert>
          )}

          {(!email || !token) && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              Invalid or missing reset link. Please use the link from your email.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || !!searchParams.get('email')}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              helperText="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
            />
            {newPassword && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.strength}
                    color={passwordStrength.color}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {passwordStrength.label}
                  </Typography>
                </Box>
              </Box>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !email || !token}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Typography variant="body2" color="primary">
                  Back to Sign In
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPasswordPage;
